import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchClearingDetail,
  fetchClearingDiscussions,
  fetchClearingDump,
  updateClearingContent,
} from '../actions/clearingActions';
import { ClearingDetail, ClearingDumpResult } from '../types/terrain.types';

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface ClearingState {
  detail: ClearingDetail | null;
  detailStatus: LoadStatus;
  cachedSlug: string | null;
  lastFetched: number | null;
  discussions: BlogDiscussionsListSuccessType;
  dump: ClearingDumpResult | null;
  dumpStatus: LoadStatus;
}

const emptyDiscussions: BlogDiscussionsListSuccessType = {
  count: 0,
  next: null,
  previous: null,
  results: [],
};

const initialState: ClearingState = {
  detail: null,
  detailStatus: 'idle',
  cachedSlug: null,
  lastFetched: null,
  discussions: emptyDiscussions,
  dump: null,
  dumpStatus: 'idle',
};

const clearingSlice = createSlice({
  name: 'clearing',
  initialState,
  reducers: {
    resetClearingState: () => initialState,
    resetDump: (state) => {
      state.dump = null;
      state.dumpStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClearingDetail.pending, (state) => {
        state.detailStatus = 'loading';
        state.detail = null;
        // Keep cachedSlug/lastFetched so the cache guard in the thunk can read
        // them before the pending action fires — nulling them here would make
        // the TTL check unreachable.
      })
      .addCase(fetchClearingDetail.fulfilled, (state, action: PayloadAction<ClearingDetail>) => {
        state.detail = action.payload;
        state.detailStatus = 'loaded';
        state.cachedSlug = action.payload.slug;
        state.lastFetched = Date.now();
      })
      .addCase(fetchClearingDetail.rejected, (state) => {
        state.detailStatus = 'error';
      })

      .addCase(fetchClearingDiscussions.fulfilled, (state, action: PayloadAction<BlogDiscussionsListSuccessType>) => {
        state.discussions = action.payload;
      })

      .addCase(fetchClearingDump.pending, (state) => {
        state.dumpStatus = 'loading';
      })
      .addCase(fetchClearingDump.fulfilled, (state, action: PayloadAction<ClearingDumpResult>) => {
        if (!state.dump || action.payload.page === 1) {
          state.dump = action.payload;
        } else {
          // Append page results
          const prev = state.dump;
          state.dump = {
            ...action.payload,
            articles: action.payload.articles
              ? [...(prev.articles || []), ...action.payload.articles]
              : null,
            // featured_articles are not paginated — keep the page-1 copy
            featured_articles: prev.featured_articles ?? action.payload.featured_articles,
            blogs: action.payload.blogs
              ? [...(prev.blogs || []), ...action.payload.blogs]
              : null,
          };
        }
        state.dumpStatus = 'loaded';
      })
      .addCase(fetchClearingDump.rejected, (state) => {
        state.dumpStatus = 'error';
      })

      .addCase(updateClearingContent.fulfilled, (state, action: PayloadAction<ClearingDetail>) => {
        state.detail = action.payload;
      });
  },
});

export const { resetClearingState, resetDump } = clearingSlice.actions;
export default clearingSlice.reducer;
